import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DetailedBookingIMPData,
    DetailedBookingSOVData,
    IMPContextualNodeData,
    MultiSlotDetailedFormProps,
    SlotEntry,
    SlotEntryData,
    SlotPackage,
    SOVContextualNodeData,
} from '../interface/DetailedBookingInterface';
import { ContextualNode } from '../interface/BookingFormInterface';
import { DetailedFormType } from '../constants/DetailedBookingConstants';
import { COMMON_FIELD_IDS } from '../config/detailedFormFieldsConfig';
import { NON_CONTEXTUAL_PACKAGE_NAME } from '../../../constants/formConstants';
import { buildSlotToSegmentName } from '../services/helpers';

export const useMultiSlotDetailedForm = ({
    initialData,
    entryId,
    isEditMode,
    detailedFormType,
    existingContextualNodes,
    filteredSlotPackages,
    validateFormTrigger,
    onAllSlotsSubmit,
}: MultiSlotDetailedFormProps) => {
    const { t } = useTranslation();

    const [slotEntries, setSlotEntries] = useState<SlotEntry[]>([
        { id: 'slot-1', collectedData: null }, // to show user an initial empty slot
    ]);

    const [slotIdToSlotNameMap, setSlotIdToSlotNameMap] = useState<
        Record<string, string>
    >({});
    const [slotIdToSegmentNameMap, setSlotIdToSegmentNameMap] = useState<
        Record<string, string>
    >({});
    const [slotIdToValidationTriggerMap, setSlotIdToValidationTriggerMap] = useState<
        Record<string, boolean>
    >({});
    const isSubmitPendingRef = useRef(false);
    const collectedDataRef = useRef<Map<string, SlotEntryData>>(new Map());
    const slotIdCounterRef = useRef(1);
    const slotEntriesRef = useRef(slotEntries);
    slotEntriesRef.current = slotEntries;
    const [isBudgetLimitModalOpen, setIsBudgetLimitModalOpen] = useState(false);
    const roValue = initialData.exactBillingValue || 0;

    const [slotBudgetMap, setSlotBudgetMap] = useState<Record<string, number>>({});

    const handleSlotBudgetChange = useCallback((slotId: string, totalBudget: number) => {
        setSlotBudgetMap((prev) => ({
            ...prev,
            [slotId]: totalBudget,
        }));
    }, []);

    const getBudgetUsedByOtherSlots = useCallback((currentSlotId: string): number => {
        return Object.entries(slotBudgetMap)
            .filter(([id]) => id !== currentSlotId)
            .reduce((sum, [, budget]) => sum + budget, 0);
    }, [slotBudgetMap]);

    const [slotIdToExpandedAccordionMap, setSlotIdToExpandedAccordionMap] = useState<Record<string, boolean>>({
        'slot-1': true,
    });

    const toggleAccordion = useCallback((slotId: string) => {
        setSlotIdToExpandedAccordionMap((prev) => ({
            ...prev,
            [slotId]: !prev[slotId],
        }));
    }, []);

    const getSlotDisplayName = useCallback(
        (slotId: string): string => {
            const selectedName = slotIdToSlotNameMap[slotId];
            if (selectedName) return selectedName;
            return t('slot-label');
        },
        [slotIdToSlotNameMap]
    );

    useEffect(() => {
        if (
            isEditMode &&
            existingContextualNodes &&
            existingContextualNodes.length > 0
        ) {
            const slotGroups = buildSlotToSegmentName(existingContextualNodes);

            const entries: SlotEntry[] = [];
            const initialSlotIdToSlotNameMap: Record<string, string> = {};
            const initialSlotIdToSegmentNameMap: Record<string, string> = {};
            slotGroups.forEach((nodes, groupKey) => {
                slotIdCounterRef.current += 1;
                const entryId = `slot-${slotIdCounterRef.current}`;
                entries.push({ id: entryId, collectedData: null });
                const isNonContextual =
                    nodes[0]?.slotOrPackageName?.toLowerCase() ===
                    NON_CONTEXTUAL_PACKAGE_NAME;
                const dropdownValue = isNonContextual
                    ? nodes[0]?.contextualNode || groupKey
                    : groupKey;

                initialSlotIdToSlotNameMap[entryId] = dropdownValue;

                initialSlotIdToSegmentNameMap[entryId] = groupKey;
            });

            if (entries.length > 0) {
                setSlotEntries(entries);
                setSlotIdToSlotNameMap(initialSlotIdToSlotNameMap);
                setSlotIdToSegmentNameMap(initialSlotIdToSegmentNameMap);
                const initialExpandedState: Record<string, boolean> = {};
                entries.forEach((entry, idx) => {
                    initialExpandedState[entry.id] = idx === 0;
                });
                setSlotIdToExpandedAccordionMap(initialExpandedState);
            }
        }
    }, [isEditMode, existingContextualNodes]);

    const previousValidateFormTriggerRef = useRef<boolean | undefined>(undefined);

    useEffect(() => {
        if (previousValidateFormTriggerRef.current === undefined) {
            previousValidateFormTriggerRef.current = validateFormTrigger;
            return;
        }
        if (previousValidateFormTriggerRef.current === validateFormTrigger) return;
        previousValidateFormTriggerRef.current = validateFormTrigger;

        collectedDataRef.current.clear();
        isSubmitPendingRef.current = true;

        setSlotIdToExpandedAccordionMap((prev) => {
            const updated = { ...prev };
            slotEntriesRef.current.forEach((entry) => {
                updated[entry.id] = true;
            });
            return updated;
        });

        setSlotIdToValidationTriggerMap((prev) => {
            const newTriggers: Record<string, boolean> = {};
            slotEntriesRef.current.forEach((entry) => {
                newTriggers[entry.id] = !prev[entry.id];
            });
            return newTriggers;
        });
    }, [validateFormTrigger]);

    const handleSlotFormSubmit = (
        slotId: string,
        formData: DetailedBookingIMPData | DetailedBookingSOVData,
        contextualNodes: IMPContextualNodeData[] | SOVContextualNodeData[]
    ) => {
        const entryData: SlotEntryData = {
            formData,
            contextualNodes,
            detailedFormType:
                detailedFormType === DetailedFormType.IMP
                    ? DetailedFormType.IMP
                    : DetailedFormType.SOV,
        };

        collectedDataRef.current.set(slotId, entryData);
        const currentSlotEntries = slotEntriesRef.current;

        if (
            isSubmitPendingRef.current &&
            collectedDataRef.current.size === currentSlotEntries.length
        ) {
            isSubmitPendingRef.current = false;
            const allData: SlotEntryData[] = currentSlotEntries
                .map((entry) => collectedDataRef.current.get(entry.id))
                .filter(Boolean) as SlotEntryData[];

            let totalBudgetAcrossSlots = 0;

            if (detailedFormType === DetailedFormType.SOV) {
                totalBudgetAcrossSlots = allData.reduce((sum, entry) => {
                    const sovFormData = entry.formData as DetailedBookingSOVData;
                    return sum + (sovFormData.valueBlockedByLineItem || 0);
                }, 0);
            } else if (detailedFormType === DetailedFormType.IMP) {
                totalBudgetAcrossSlots = allData.reduce((sum, entry) => {
                    const impContextualNodes =
                        entry.contextualNodes as IMPContextualNodeData[];
                    const slotTotal = impContextualNodes.reduce(
                        (nodeSum, node) =>
                            nodeSum + (node.valueToBeLockedAtPackageLevel || 0),
                        0
                    );
                    return sum + slotTotal;
                }, 0);
            }

            if (roValue > 0 && totalBudgetAcrossSlots > roValue) {
                setIsBudgetLimitModalOpen(true);
                return;
            }

            onAllSlotsSubmit(allData);
        }
    };

    const handleAddSlot = () => {
        slotIdCounterRef.current += 1;
        const newId = `slot-${slotIdCounterRef.current}`;
        setSlotEntries((prev) => [...prev, { id: newId, collectedData: null }]);

        setSlotIdToExpandedAccordionMap((prev) => {
            const collapsed: Record<string, boolean> = {};
            Object.keys(prev).forEach((key) => {
                collapsed[key] = false;
            });
            collapsed[newId] = true;
            return collapsed;
        });
    };

    const handleSlotSelectionChange = (slotId: string, slotName: string) => {
        setSlotIdToSlotNameMap((prev) => ({
            ...prev,
            [slotId]: slotName,
        }));
    };

    const getPreSelectedSlotNames = (currentSlotId: string): string[] => {
        return Object.entries(slotIdToSlotNameMap)
            .filter(([id, name]) => id !== currentSlotId && name)
            .map(([, name]) => name);
    };

    const handleRemoveSlot = (slotId: string) => {
        const removedData = collectedDataRef.current.get(slotId);
        if (removedData) collectedDataRef.current.delete(slotId);

        setSlotIdToSlotNameMap((prev) => {
            const updated = { ...prev };
            delete updated[slotId];
            return updated;
        });

        setSlotIdToExpandedAccordionMap((prev) => {
            const updated = { ...prev };
            delete updated[slotId];
            return updated;
        });

        setSlotIdToSegmentNameMap((prev) => {
            const updated = { ...prev };
            delete updated[slotId];
            return updated;
        });

        setSlotBudgetMap((prev) => {
            const updated = { ...prev };
            delete updated[slotId];
            return updated;
        });

        setSlotEntries((prev) => prev.filter((entry) => entry.id !== slotId));
    };

    const getAvailableSlotPackages = useCallback((currentSlotId: string): SlotPackage[] | null => {
        if (!filteredSlotPackages) return null;

        const slotNames = new Set(
            Object.entries(slotIdToSlotNameMap)
                .filter(([id, name]) => id !== currentSlotId && name)
                .map(([, name]) => name)
        );

        return filteredSlotPackages
            .filter((pkg) => !slotNames.has(pkg.slotPackageName))
            .map((pkg) => ({
                ...pkg,
                segments: pkg.segments.filter(
                    (seg) => !slotNames.has(seg.segmentName)
                ),
            }))
            .filter((pkg) => pkg.segments.length > 0);
    }, [slotIdToSlotNameMap, filteredSlotPackages]);

    const existingNodesPerSlot = useMemo(() => {
        const slotIdToContextualNodesMap: Record<string, ContextualNode[]> = {};
        if (
            !isEditMode ||
            !existingContextualNodes ||
            existingContextualNodes.length === 0
        )
            return slotIdToContextualNodesMap;

        const slotGroups = buildSlotToSegmentName(existingContextualNodes);

        Object.entries(slotIdToSegmentNameMap).forEach(([slotId, segmentName]) => {
            const nodes = slotGroups.get(segmentName);
            if (nodes) slotIdToContextualNodesMap[slotId] = nodes;
        });

        return slotIdToContextualNodesMap;
    }, [isEditMode, existingContextualNodes, slotIdToSegmentNameMap]);

    const getExistingNodesForSlot = (
        slotId: string
    ): ContextualNode[] | null => {
        return existingNodesPerSlot[slotId] || null;
    };

    const totalSelectableSlotOptions = (() => {
        if (!filteredSlotPackages || filteredSlotPackages.length === 0) return 0;
        return filteredSlotPackages.reduce((count, pkg) => {
            if (pkg.slotPackageName.toLowerCase() === NON_CONTEXTUAL_PACKAGE_NAME) {
                return count + pkg.segments.length;
            }
            return count + 1;
        }, 0);
    })();

    const canAddMoreSlots = filteredSlotPackages
        ? slotEntries.length < totalSelectableSlotOptions
        : false;

    const allSlotsSelected = (() => {
        if (!filteredSlotPackages || filteredSlotPackages.length === 0) return true;
        const selectedCount =
            Object.values(slotIdToSlotNameMap).filter(Boolean).length;
        return selectedCount >= totalSelectableSlotOptions;
    })();

    const isAddSlotDisabled = !canAddMoreSlots || allSlotsSelected;

    const commonFields = [
        {
            id: COMMON_FIELD_IDS.ENTRY_NUMBER_FIELD,
            label: 'entry-number-label',
            value: entryId?.toString() || '',
        },
        {
            id: COMMON_FIELD_IDS.BRAND_FIELD,
            label: 'brand-label',
            value: initialData.brand || '',
        },
        {
            id: COMMON_FIELD_IDS.AE_NAME_FIELD,
            label: 'ae-name-label',
            value: initialData.aeName || '',
        },
        {
            id: COMMON_FIELD_IDS.CATEGORY_FIELD,
            label: 'category-label',
            value: initialData.category || '',
        },
        {
            id: COMMON_FIELD_IDS.TYPE_OF_PURCHASE_FIELD,
            label: 'type-of-purchase-label',
            value: initialData.typeOfPurchase || '',
        },
    ];

    const handleBudgetLimitModalClose = () => setIsBudgetLimitModalOpen(false);
    return {
        commonFields,
        slotProps: {
            slotEntries,
            slotIdToExpandedAccordionMap,
            getSlotDisplayName,
            slotIdToSlotNameMap,
            toggleAccordion,
            handleSlotSelectionChange,
            getPreSelectedSlotNames,
        },
        formProps: {
            handleSlotFormSubmit,
            slotIdToValidationTriggerMap,
            getExistingNodesForSlot,
            getAvailableSlotPackages,
            handleSlotBudgetChange,
            getBudgetUsedByOtherSlots,
        },
        confirmationModalProps: {
            isBudgetLimitModalOpen,
            handleBudgetLimitModalClose,
            roValue,
        },
        iconButtonProps: { handleAddSlot, handleRemoveSlot, isAddSlotDisabled },
    };
};